<template>
  <div>
    <div class="row p-3 m-0 bg-gradient-one">
      <div v-if="batches.length > 1" class="col-12 col-md-4 hidden-overflow" style="max-height: 400px">
        <h5 class="text-center mb-1">
          <span class="font-weight-light">Total Batches:</span> <span class="font-weight-bold">{{ batches.length }}</span>
        </h5>
        <div class="d-block d-md-none">
          <BushelSelect
            :options="batches"
            :value="selectedBatch"
            track-by="id"
            label="name"
            :allow-empty="false"
            no-deselect
            thin
            class="mb-1"
            @select="(val) => $emit('setSelectedBatch', val)"
          />
        </div>
        <div class="d-none d-md-block">
          <div
            v-for="(batch, index) in batches"
            :key="batch.id"
            class="border border-primary text-center w-100 py-2 rounded my-1 clickable"
            :class="batch.id == selectedBatch.id ? 'bg-primary text-white' : 'text-primary'"
            @click="$emit('setSelectedBatch', batch)"
          >
            Batch {{ index + 1 }}
          </div>
        </div>
      </div>
      <div class="col-12" :class="batches.length > 1 ? 'col-md-4' : 'col-md-7 px-3'">
        <ProductDisplayDetailsTab :product="product" :batch="selectedBatch" :on-deck-override="onDeckOverride" />
      </div>
      <div class="col-12 text-center" :class="batches.length > 1 ? 'col-md-4' : 'col-md-5'">
        <div>
          <h4 v-if="!hideQuantities" class="text-center font-weight-bold mb-1">
            <span v-if="selectedBatch.disable_inventory_tracking">
              <i class="ml-2 fas fa-infinity fa-fw" />
              {{ getUnit(product).name }} Available
            </span>
            <span v-else-if="selectedBatch.quantity">
              {{ availableQuantity }}
              <span v-if="product.unit_measurement && product.unit_measurement.alias">{{ getUnit(product).name }} Available</span>
            </span>

            <!-- this is agregated total quantity. we don't care about this anymore
              <span v-if="checkShowTotalQuantity(batches)">
              {{ calculatedAvailableProductQuantity(batches) }} <span v-if="product.unit_measurement && product.unit_measurement.alias">{{ getUnit(product).name }}</span>
            </span>
            <span v-if="checkShowTotalQuantity(batches) && checkShowInfinityQuanity(batches)">/</span><span v-if="checkShowInfinityQuanity(batches)">
              <i class="ml-2 fas fa-infinity fa-fw" />
              {{ getUnit(product).name }}
            </span> -->
          </h4>
          <ProductDisplayStoreContainer
            v-if="selectedBatch"
            :product="product"
            :on-deck-override="onDeckOverride"
            :batch="selectedBatch"
            :store-component-enabled="storeComponentEnabled"
            :bulk-discounts="bulkDiscounts"
            :cart-items="cartItems"
            :disable-add="disableAdd || hideQuantities"
            :cart-needs-to-be-chosen="cartNeedsToBeChosen"
            :active-location="activeLocation"
            @added-product="(product) => $emit('addedProduct', product)"
            @contact-button-clicked="$emit('contactButtonClicked')"
            @launch-p-t-o-modal="(val) => $emit('launchPTOModal', val)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUnit, getUnitSize, getPackageCaseOptions } from "@/services/storefront/storefront";
import {
  calculatedAvailableProductQuantity,
  calculatedAvailableBatchQuantity,
  checkShowTotalQuantity,
  checkShowInfinityQuanity,
} from "@/services/inventory/inventory";
import { showBatchDocumentsUploadFieldCheck } from "@/services/inventory/showBatchFieldChecks";
import ProductDisplayStoreContainer from "./ProductDisplayStoreContainer.vue";
import ProductDisplayDetailsTab from "./ProductDisplayDetailsTab.vue";

export default {
  components: {
    ProductDisplayDetailsTab,
    ProductDisplayStoreContainer,
  },
  props: {
    selectedBatch: { type: Object },
    batches: { type: Array, required: true },
    cartItems: { type: Array, required: true },
    product: { type: Object, required: true },
    user: { type: Object, default: null },
    storeComponentEnabled: { type: Boolean, required: true },
    bulkDiscounts: { type: Array, required: true },
    hidePrices: { type: Boolean, required: true },
    hideQuantities: { type: Boolean, required: true },
    disableAdd: { type: Boolean, required: true },
    cartNeedsToBeChosen: { type: Boolean, required: true },
    activeLocation: { type: Object },
  },
  emits: ["setSelectedBatch", "addedProduct", "contactButtonClicked", "launchPTOModal"],
  computed: {
    showBatchDocumentsUploadField() {
      if (this.product.category) {
        return showBatchDocumentsUploadFieldCheck(this.product.category.name);
      }
      return true;
    },
    availableQuantity() {
      return this.selectedBatch.quantity - this.quantityInCart();
    },

    // if the current batch is on deck, and all the batches that are active with a sort_order less than the current batch's sort_order are in cart,
    // then this batch is the next batch to be sold and should be able to be added to cart
    // check for cart items because if no cart items than it will never be overriden, and also because
    // cart items will always be empty from order modify screen and we don't want this to apply there
    onDeckOverride() {
      if (this.selectedBatch.hold && this.cartItems && this.cartItems.length) {
        // get all the batches
        const batchesBeforeCurrent = this.product.batches.filter((batch) => batch.sort_order < this.selectedBatch.sort_order);

        // get all the items that don't have all their quantity tied up in cart
        let priorBatchQuantityAvailable = false;

        for (let i = 0; i < batchesBeforeCurrent.length; i += 1) {
          if (!priorBatchQuantityAvailable) {
            const foundCartItem = this.cartItems.find((item) => item.batch_id == batchesBeforeCurrent[i].id && !item.sample);
            if (!foundCartItem) {
              // this means that there is an item with a sort order above checking item that is not in cart.
              priorBatchQuantityAvailable = true;
            } else if (foundCartItem.quantity < batchesBeforeCurrent[i].quantity) {
              // if there is a batch that still has quantity remaining
              priorBatchQuantityAvailable = true;
            }
          }
        }
        if (priorBatchQuantityAvailable) {
          return false;
        }

        return true;
      }
      return false;
    },
  },
  methods: {
    calculatedAvailableProductQuantity,
    calculatedAvailableBatchQuantity,
    checkShowTotalQuantity,
    checkShowInfinityQuanity,
    getUnit,
    getUnitSize,
    getPackageCaseOptions,
    medRec() {
      const baseClasses = "dom d-inline-block small px-2 py-1 text-white bg-success";
      return baseClasses;
    },
    quantityInCart() {
      if (this.cartItems && this.cartItems.length) {
        const foundItem = this.cartItems.find((item) => item.batch_id == this.selectedBatch.id && !item.sample);
        if (foundItem) {
          return foundItem.quantity;
        }
      }
      return 0;
    },
    showInfinity(batch) {
      let showInf = false;
      let hasPotency = false;
      if (this.showBatchDocumentsUploadField) {
        if (batch.documents && batch.documents.length > 0) {
          batch.documents.forEach((doc, index) => {
            if (doc.label == "Potency") {
              hasPotency = true;
            }
          });
        }
        if (hasPotency && batch.thc_limit) {
          showInf = true;
        }
      } else {
        showInf = true;
      }
      return showInf;
    },
  },
};
</script>
